import React, { useState, useMemo, useEffect } from 'react';

import { featureFlagsSelectors } from 'app-components/flux/feature-flags';
import * as tickerLogosSelectors from 'app-components/flux/ticker-logos/tickerLogosSelectors';
import { tracker } from 'app-components/utils/core';

import AttachMoneyRoundedIcon from '@mui/icons-material/AttachMoneyRounded';
import TrendingUpRoundedIcon from '@mui/icons-material/TrendingUpRounded';

import Avatar from '@mui/material/Avatar';
import type { AvatarProps } from '@mui/material/Avatar';
import type { Theme } from '@mui/material/styles';
import { useTheme } from '@mui/material/styles';
import Badge from '@mui/material/Badge';
import CurrencyBitcoinRoundedIcon from '@mui/icons-material/CurrencyBitcoinRounded';

import { useAppSelector } from 'hooks/redux';

const medium = 32;
const large = 40;

type AssetTypeConfig = {
  icon: React.ElementType;
  iconColor: 'success' | 'primary' | 'secondary' | 'inherit';
  backgroundColor: string;
  color: string;
};

const getAssetTypeConfig = (theme: Theme): Record<string, AssetTypeConfig> => ({
  cash: {
    icon: AttachMoneyRoundedIcon,
    iconColor: 'success',
    backgroundColor: theme.palette.success.light,
    color: theme.palette.success.main,
  },
  stock: {
    icon: TrendingUpRoundedIcon,
    iconColor: 'inherit',
    backgroundColor: theme.palette.primary.light,
    color: theme.palette.primary.main,
  },
  crypto: {
    icon: TrendingUpRoundedIcon,
    iconColor: 'inherit',
    backgroundColor: theme.palette.primary.light,
    color: theme.palette.primary.main,
  },
});

export type StockLogoProps = AvatarProps & {
  type?: 'stock' | 'cash' | 'crypto';
  value?: string;
  size?: 'medium' | 'large';
};

export const TickerAvatar = (props: StockLogoProps) => {
  const { value, size, type = 'stock', sx, slotProps, ...otherProps } = props;
  const theme = useTheme();
  const [error, setError] = useState(false);
  const tickerLogo = useAppSelector((state) => tickerLogosSelectors.getTickerLogoByKey(state, value));
  const assetConfig = getAssetTypeConfig(theme);
  const sxOverride = useMemo(() => {
    const sizeSx =
      size === 'medium' ? { width: medium, height: medium } : size === 'large' ? { width: large, height: large } : {};
    return { ...sizeSx, ...sx };
  }, [size, sx]);

  const trackLogoTicker = useAppSelector((state) => featureFlagsSelectors.getFeatureFlag(state, 'trackLogoTicker'));
  const tickerValidate = useAppSelector((state) => featureFlagsSelectors.getFeatureFlag(state, 'tickerValidate'));
  const valid = useMemo(() => Boolean(value?.match(tickerValidate || '')), [tickerValidate, value]);
  useEffect(() => {
    if (trackLogoTicker && value && !tickerLogo?.logoUrl) {
      tracker.track(tracker.events.logoTicker, { ticker: value, valid, url: tickerLogo?.logoUrl, status: 'Not Found' });
    }
  }, [tickerLogo?.logoUrl, value, trackLogoTicker, valid]);

  const config = assetConfig[type];
  const FallbackIcon = config?.icon;

  return (
    <Badge
      overlap='circular'
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      badgeContent={<CurrencyBitcoinRoundedIcon sx={{ fontSize: 12 }} />}
      variant='standard'
      invisible={type !== 'crypto'}
      sx={{
        '& .MuiBadge-badge': {
          width: 14,
          height: 14,
          minWidth: 14,
          padding: 0,
          backgroundColor: 'background.paper',
          border: '1px solid',
          borderColor: 'divider',
          boxShadow: 1,
        },
      }}
    >
      {(type !== 'stock' && type !== 'crypto') || error || !tickerLogo?.logoUrl ? (
        <Avatar
          sharedcomponentid='TickerAvatar'
          id={`stock-logo-${value}`}
          alt={`${value} logo`}
          variant='rounded'
          sx={{ backgroundColor: config.backgroundColor, color: config.color, ...sxOverride }}
          {...otherProps}
        >
          <FallbackIcon color={config.iconColor} />
        </Avatar>
      ) : (
        <Avatar
          sharedcomponentid='TickerAvatar'
          id={`stock-logo-${value}`}
          alt={`${value} logo`}
          variant='rounded'
          src={tickerLogo?.logoUrl}
          onError={() => {
            if (trackLogoTicker) {
              tracker.track(tracker.events.logoTicker, {
                ticker: value,
                valid,
                url: tickerLogo?.logoUrl,
                status: 'Failed',
              });
            }
            setError(true);
          }}
          onLoad={() => {
            if (trackLogoTicker) {
              tracker.track(tracker.events.logoTicker, {
                ticker: value,
                valid,
                url: tickerLogo?.logoUrl,
                status: 'Succeed',
              });
            }
            setError(false);
          }}
          sx={sxOverride}
          slotProps={{
            img: {
              sx: {
                objectFit: 'contain',
                backgroundColor: theme.palette.background.paper,
              },
              ...slotProps?.img,
            },
            ...slotProps,
          }}
          {...otherProps}
        />
      )}
    </Badge>
  );
};

export default TickerAvatar;
